import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BarChart3, MailCheck, MailX, Eye, MousePointerClick } from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';

/**
 * AdminDeliverabilityTile — last-30-day email stats per template.
 * Reads from /api/admin/email-deliverability (aggregated from the
 * `email_deliveries` MongoDB collection that every `send_templated_email`
 * call appends to). Opens/clicks/bounces are populated by the Resend
 * webhook at /api/admin/email-webhook/resend (configure in Resend
 * dashboard → Webhooks).
 */
const AdminDeliverabilityTile = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await apiFetch('/api/admin/email-deliverability');
      if (data) setStats(data);
      setLoading(false);
    })();
  }, []);

  const pct = (n, d) => (d > 0 ? Math.round((n / d) * 100) : 0);

  return (
    <Card data-testid="admin-deliverability-tile">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <BarChart3 className="h-4 w-4 text-[#00A896]" />
          Email deliverability (last 30 days)
        </CardTitle>
        <CardDescription className="text-xs">
          Sent / delivered / opened / bounced — grouped by template.
          {!loading && stats && stats.totals.opened === 0 && stats.totals.delivered > 0 && (
            <span className="block mt-1 text-amber-600 dark:text-amber-400">
              No open/click events yet — wire the Resend webhook at <code className="text-[10px]">/api/admin/email-webhook/resend</code> to populate.
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : !stats || stats.totals.sent === 0 ? (
          <p className="text-sm text-muted-foreground">No emails sent in the last 30 days yet.</p>
        ) : (
          <>
            {/* Totals row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="deliverability-totals">
              <Stat label="Sent" value={stats.totals.sent} icon={MailCheck} color="text-[#00A896]" />
              <Stat label="Delivered" value={stats.totals.delivered} sub={`${pct(stats.totals.delivered, stats.totals.sent)}%`} icon={MailCheck} color="text-green-600" />
              <Stat label="Opened" value={stats.totals.opened} sub={stats.totals.delivered > 0 ? `${pct(stats.totals.opened, stats.totals.delivered)}%` : ''} icon={Eye} color="text-blue-600" />
              <Stat label="Bounced" value={stats.totals.bounced + stats.totals.complained + stats.totals.failed} icon={MailX} color={(stats.totals.bounced + stats.totals.complained + stats.totals.failed) > 0 ? 'text-red-600' : 'text-muted-foreground'} />
            </div>

            {/* Per-template table */}
            <div className="rounded border overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="text-left p-2 font-medium">Template</th>
                    <th className="text-right p-2 font-medium">Sent</th>
                    <th className="text-right p-2 font-medium">Delivered</th>
                    <th className="text-right p-2 font-medium">Opened</th>
                    <th className="text-right p-2 font-medium">Issues</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.by_template.map((row) => {
                    const issues = (row.failed || 0) + (row.bounced || 0) + (row.complained || 0);
                    return (
                      <tr key={row.template_key} className="border-t" data-testid={`deliv-row-${row.template_key}`}>
                        <td className="p-2 font-mono text-[11px]">{row.template_key}</td>
                        <td className="p-2 text-right">{row.sent}</td>
                        <td className="p-2 text-right text-green-700 dark:text-green-400">{row.delivered}</td>
                        <td className="p-2 text-right">
                          {row.opened}
                          {row.delivered > 0 && (
                            <span className="text-[10px] text-muted-foreground ml-1">({pct(row.opened, row.delivered)}%)</span>
                          )}
                        </td>
                        <td className="p-2 text-right">
                          {issues > 0 ? (
                            <Badge variant="destructive" className="text-[10px]">{issues}</Badge>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const Stat = ({ label, value, sub, icon: Icon, color }) => (
  <div className="rounded border p-2">
    <div className={`flex items-center gap-1 text-[10px] uppercase font-semibold ${color}`}>
      <Icon className="h-3 w-3" /> {label}
    </div>
    <div className="text-xl font-bold mt-0.5">{value}</div>
    {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
  </div>
);

export default AdminDeliverabilityTile;
